<template lang="pug">
.theindex-post
  err404(v-if="error")
  post-skeleton(v-else-if="!loaded" :show-hero="skeletonShowHero" :show-video="skeletonShowVideo" :show-documentary="skeletonShowDocumentary")
  template(v-else-if="post")
    the-index-header(:category="post.category" :title="post.title?.rendered"  :showBreadcrumbs="!$rogue.isApp()")
    .headline(v-if="$rogue.isApp()")
    post-default(v-if="postType === 'postDefault'" :post="post" :author-list="authorWhitelist")
    component(v-else :is="componentMap[postType]" :post="post" :author-list="authorWhitelist")
    .main-container.featured-products-container(v-if="showFeaturedProducts")
      h2 {{additional.title}}
      ProductCarousel(:category="featuredProductsUrl" :groupId="constructorGroupId")
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onServerPrefetch, ref, watch } from 'vue'
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import type { ITheIndexPost, YoutubeVideo } from './types'
import metadata, { type MetadataType } from '@/js/helpers/metadata'
import { messages } from '@/js/app-bridge/messages'
import postAthlete from '@/js/pages/theindex/post-types/athlete.vue'
import postDefault from '@/js/pages/theindex/post-types/default.vue'
import postEvent from '@/js/pages/theindex/post-types/event.vue'
import postGallery from '@/js/pages/theindex/post-types/gallery.vue'
import postDocumentary from '@/js/pages/theindex/post-types/documentary.vue'
import theIndexHeader from '@/js/pages/theindex/components/header.vue'
import postSkeleton from '@/js/pages/theindex/components/post-skeleton.vue'
import { getPostBySlug } from '@/js/pages/theindex/helpers/search'
import { useHttpStore } from '@/js/stores/httpStore'
import { useRogue } from '@/js/services/Rogue'
import { THE_INDEX_CATEGORY_IDS, THE_INDEX_TITLES_NS } from './constants'

type VideoData = {
  '@context' : string,
  '@type' : string,
  name : string,
  description : string,
  uploadDate : string,
  thumbnailUrl : string,
  contentUrl : string,
  embedUrl : string,
}

const httpStore = useHttpStore()
const route = useRoute()
const rogue = useRogue()

const cachedData = (!rogue.ssr)
  ? (httpStore.cache[`index-${route.params.slug}`] as {error?: boolean})
  : null

const post = ref<Partial<ITheIndexPost>>({})
const error = ref<boolean>(cachedData?.error || false)
const loaded = ref<boolean>(false)
const postType = ref<'postDefault' | 'postGallery' | 'postAthlete' | 'postEvent' | 'postDocumentary'>('postDefault')
const videoDataList = ref<VideoData[]>([])
const videoDataReady = ref<boolean>(false)

const componentMap = {
  postDefault     : postDefault,
  postGallery     : postGallery,
  postAthlete     : postAthlete,
  postEvent       : postEvent,
  postDocumentary : postDocumentary,
}

const head = computed<MetadataType>(() => ({
  title       : post.value?.title?.rendered ? rogue.__(post.value.title.rendered.replace('&#8217;', '\''), THE_INDEX_TITLES_NS) : rogue.__('The Index'),
  description : post.value?.excerpt?.rendered
    ? rogue.__(post.value.excerpt.rendered, THE_INDEX_TITLES_NS)
    : rogue.__('A knowledge base of product and movement information, lifestyle content and strength history.'),
  image  : post.value?.featuredImagePath || '/theindex/2023%20Headers/new%20-%202000%20x%20300/home_ia101k.jpg',
  robots : 'all',
  url    : rogue.localizedBaseURL + route.path,
  script : videoDataReady.value ? videoDataList.value.map((data, index) => ({
    type      : 'application/ld+json',
    key       : `post-video-${index}-ld-json`,
    innerHTML : JSON.stringify(data),
  })) : undefined,
}))

const headControl = useHead(metadata(head.value, rogue))

const additional = computed<{categoryParent: string, categorySlug?: string, categoryId?: string}>(() => JSON.parse(post.value?.customJs || '{}'))

const featuredProductsUrl = computed<string>(() => `/${additional.value.categoryParent}/${additional.value.categorySlug || route.params.slug}`)
const constructorGroupId = computed<string | undefined>(() => additional.value.categoryId?.toString())
  
const showFeaturedProducts = computed<boolean>(() => {
  if (postType.value === 'postAthlete') return false
  const useConstructor = rogue.featureFlags.isOn('enable_constructor')

  return useConstructor ? !!constructorGroupId.value : !!additional.value.categorySlug
})

const authorWhitelist = computed(() => {
  return rogue.featureFlags.isOn('author_enabled') ? ['johnson', 'luikart'] : []
})

const skeletonShowHero = computed(() => route.params.catchAll === 'athletes')

// A prior listing page (posts-grid.vue) may have already cached this post's real
// category/format under index-{query} - use that instead of guessing from the URL category,
// since a category can mix post types and formats.
function findCachedListingMatch(slug: string): ITheIndexPost | undefined {
  const listingCacheKeys = Object.keys(httpStore.cache).filter(key => key.startsWith('index-{'))

  for (const key of listingCacheKeys) {
    const cached = httpStore.cache[key] as { posts?: ITheIndexPost[] } | undefined
    const match = cached?.posts?.find(cachedPost => cachedPost.slug === slug)
    if (match) {
      return match
    }
  }

  return undefined
}

const cachedListingMatch = computed(() => findCachedListingMatch(route.params.slug as string))

const skeletonShowVideo = computed(() => {
  if (cachedListingMatch.value) {
    return cachedListingMatch.value.format === 'video'
  }

  const category = route.params.catchAll as string

  if (category === 'movement-library') {
    return (route.params.slug as string)?.includes('demo') ?? false
  }

  return ['gym-tours', 'lifestyle', 'documentary'].includes(category)
})

const skeletonShowDocumentary = computed(() => {
  return cachedListingMatch.value?.category?.id === THE_INDEX_CATEGORY_IDS.DOCUMENTARY && cachedListingMatch.value?.format === 'standard'
})

watch(() => route.params.slug, (newVal, oldVal) => {
  if (newVal !== oldVal || !post.value?.id) {
    load()
  }
})

watch(() => videoDataList.value, updateHead)

onServerPrefetch(async () => {
  await load()
  // Include both post and pre-stringified video data in cache
  httpStore.setCache(`index-${route.params.slug}`, {
    post           : post.value,
    videoDataList  : videoDataList.value,
    videoDataReady : videoDataReady.value,
    error          : error.value,
  })

  headControl.patch(metadata(head.value, rogue))
})

onBeforeMount(() => {
  const {
    post: cachedPost,
    videoDataList: cachedVideoDataList,
    videoDataReady: cachedVideoDataReady,
  } = httpStore.cache[`index-${route.params.slug}`] as { post: ITheIndexPost, videoDataList: VideoData[], videoDataReady: boolean }
    || {} as Partial<{ post: ITheIndexPost, videoDataList: VideoData[], videoDataReady: boolean }>

  post.value = cachedPost

  if (!post.value?.id) {
    load()
  } else {
    init()

    // Restore video data from cache if available
    if (cachedVideoDataList) {
      videoDataList.value = cachedVideoDataList
      videoDataReady.value = cachedVideoDataReady || false
    }

    loaded.value = true
  }
})

function updateHead() {
  headControl.patch(metadata(head.value, rogue))
}

async function load() {
  loaded.value = false
  videoDataReady.value = false

  try{
    let loadedPost = await getPostBySlug(rogue, route.params.slug as string)

    if (loadedPost) {
      loadedPost.content = { rendered: transformPost(loadedPost.content.rendered) }
      post.value = loadedPost
      init()

      // Extract video IDs for both server and client
      const videoIds = scrapeVideoIds(loadedPost.content.rendered)
      if (videoIds.length > 0) {
        await generateVideoData(videoIds)
      } else {
      // Ensure videoDataReady is set even if no videos
        videoDataReady.value = true
      }
    } else{
      error.value = true
    }
  }
  catch (e) {
    //
  }

  if (rogue.appContext) {
    rogue.emitToApp(messages.PAGE_OPENED, {
      url   : `${rogue.baseURL}${route.fullPath}`,
      type  : 'theindex/post',
      title : post.value.title?.rendered,
    })
  }

  loaded.value = true
}

function init(): void {
  const id = post.value.category?.id || post.value.categories?.[0]

  if (post.value.format === 'gallery') {
    postType.value = 'postGallery'
  } else if (id === THE_INDEX_CATEGORY_IDS.ATHLETES) {
    postType.value = 'postAthlete'
  } else if (id === THE_INDEX_CATEGORY_IDS.EVENTS && post.value.customJs) {
    postType.value = 'postEvent'
  } else if (id === THE_INDEX_CATEGORY_IDS.DOCUMENTARY && post.value.format === 'standard') {
    postType.value = 'postDocumentary'
  } else {
    postType.value = 'postDefault'
  }
}

function transformPost(content: string): string {
  // google-drive-embed
  content = content.replace(
    /\[google-drive-embed[^\]]*\]/,
    (match) => {
      const src = /https:\/\/drive\.google\.com\/file\/d\/([^/]+)\/preview/.exec(match)

      return src
        ? `<iframe width="100%" height="1180" frameborder="0" scrolling="yes" class="iframe-embed" src="https://drive.google.com/file/d/${src[1]}/preview" allowfullscreen=""></iframe>`
        : match
    }
  )

  return content
}

function scrapeVideoIds(content: string): string[] {
  // All regex patterns to extract YouTube video IDs
  const patterns = [
    // Iframe embeds
    /(?:<iframe[^>]*)(?:src="https:\/\/www\.youtube\.com\/embed\/|src="https:\/\/www\.youtube-nocookie\.com\/embed\/)([a-zA-Z0-9_-]{11})(?:[?&][^"]*)?["]/g,

    // Shortcodes
    /\[(?:youtube|yt)[ =]([a-zA-Z0-9_-]{11})[\] ]/g,
  ]

  // Using flatMap to process all patterns and collect results in one step
  const videoIds = patterns.flatMap(pattern => {
    // Use matchAll to get all matches at once
    const matches = Array.from(content.matchAll(pattern))

    // Extract the video ID (first capture group) from each match
    return matches.map(match => match[1])
  })

  // Remove duplicates with Set and return
  return [...new Set(videoIds)]
}

async function generateVideoData(videoIds: string[]): Promise<void> {
  const videoDataPromises = videoIds.map(async (videoId) => {
    try {
      const additionalVideoData = await rogue.fetch<YoutubeVideo[]>(`/svc_ssr/video-detail/?videoId=${videoId}`)
      if (additionalVideoData?.[0]?.snippet) {
        const snippet = additionalVideoData[0].snippet
        const contentDetails = additionalVideoData[0]?.contentDetails?.duration
        const cleanDescription = snippet.description ? snippet.description.replace(/^https?:\/\/[^\s]+\n*/g, '').trim().slice(0, 500) : ''

        return {
          '@context'   : 'https://schema.org',
          '@type'      : 'VideoObject',
          name         : snippet.title,
          description  : cleanDescription,
          uploadDate   : snippet.publishedAt,
          duration     : contentDetails,
          thumbnailUrl : snippet.thumbnails.maxres?.url ||
                         snippet.thumbnails.high?.url ||
                         snippet.thumbnails.standard?.url ||
                         snippet.thumbnails.medium?.url ||
                         snippet.thumbnails.default?.url,
          contentUrl : `https://www.youtube.com/watch?v=${videoId}`,
          embedUrl   : `https://www.youtube.com/embed/${videoId}`,
        }
      }
    } catch (ex) {
      rogue.log.warn('Failed to fetch additional video details.', ex)
    }

    return null
  })

  const videoData = await Promise.all(videoDataPromises)
  videoDataList.value = videoData.filter(data => data !== null)
  videoDataReady.value = true
}
</script>

<style lang="scss" scoped>
.theindex-post {
  margin-bottom: 60px;
}
</style>

<style lang="scss">
.theindex-post iframe:not(.overlay-iframe, .video)[src*=youtube] {
  position: relative;
  width: calc(100vw - 48px);
  width: calc(100vw - 48px);
  left: 50%;
  margin-left: calc(-50vw + 24px);
  height: calc((100vw - 48px) * 9 / 16);

  @media(min-width: 1280px) {
    width: calc(1280px - 24px);
    margin-left: calc(-640px + 12px);
    height: calc((1280px - 24px) * 9 / 16);
  }

  @media(min-width: 1304px) {
    width: 1280px;
    margin-left: -640px;
    height: calc(1280px * 9 / 16);
  }
}

.theindex-post a.button {
  margin-top: $space-s;
  display: inline-block;
  padding: $size-xxs $size-s;
  user-select: none;
  border-radius: 3px;
  color: $color-white;
  background: linear-gradient(180deg, $color-red 0%, $color-red-dark 100%);
  text-decoration: none;

  &:focus {
    background: $color-red-dark;
  }

  &:hover {
    background: linear-gradient(180deg, $color-red-light 0%, $color-red 100%);
  }

  &.button-black {
    background: linear-gradient(180deg, $color-black-light 0%, $color-black 100%);

    &:focus {
      background: $color-black;
    }

    &:hover {
      background: $color-black-light;
    }
  }
}

.theindex-post .main-container-small {
  max-width: 800px;
}

.theindex-post h1 {
  font-family: $font-base;
  text-transform: none;
}

.theindex-post p {
  line-height: 30px;
  margin-bottom: 32px;
}

.theindex-post .main-container-small img {
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  display: block;
}

.theindex-post .iframe-embed {
  display: none;

  @include not-mobile() {
    display: block;
  }
}

.theindex-post .mobile-image {
  display: block;

  @include not-mobile() {
    display: none !important;
  }
}

.theindex-post .social-sharing {
  justify-content: flex-start;
}

.headline {
  margin-top: 24px;
}
</style>
